import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import rushLogo from './rush-logo.png';
import './rush.css';
import './auth.css';
import { loginSuccess } from '../../slices/authSlice';
import { storeAuthToken } from '../../utils/authToken';

// Renders the TOTP code prompt after /login or /magic-link/consume returns
// needs2FA: 'challenge'. The challenge token is in the URL (?ct=…). Code
// goes to POST /api/2fa/challenge which returns the real auth JWT.
const RushTwoFactorChallengePage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { challengeToken, rememberMe } = useMemo(() => {
        const qs = new URLSearchParams(location.search);
        return {
            challengeToken: qs.get('ct') || '',
            rememberMe: qs.get('r') === '1',
        };
    }, [location.search]);

    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (code.length !== 6) {
            setError('Enter the 6-digit code from your authenticator app.');
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post('/api/2fa/challenge', {
                challengeToken,
                code,
                rememberMe,
            });
            const token = res.data.token;
            storeAuthToken(token, rememberMe);
            const me = await axios.get('/api/users/me', {
                headers: { Authorization: `Bearer ${token}` },
            });
            dispatch(loginSuccess({ token, user: me.data }));
            navigate('/dashboard', { replace: true });
        } catch (err) {
            setError(err.response?.data?.msg || 'Could not verify code.');
            setSubmitting(false);
        }
    };

    if (!challengeToken) {
        return (
            <div className="rs auth-page">
                <section className="auth-form-panel">
                    <div className="auth-form-body">
                        <h1 className="auth-title">Challenge expired</h1>
                        <p className="auth-sub">
                            Please <Link to="/login">sign in again</Link> to get a new code prompt.
                        </p>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="rs auth-page">
            <section className="auth-form-panel">
                <header className="auth-form-header">
                    <Link to="/" className="rs-logo rs-logo-img">
                        <img src={rushLogo} alt="Rush Studio" />
                    </Link>
                </header>
                <div className="auth-form-body">
                    <div className="auth-eyebrow">Two-factor authentication</div>
                    <h1 className="auth-title">Enter your code.</h1>
                    <p className="auth-sub">
                        Open your authenticator app (Authy, Google Authenticator, 1Password…)
                        and enter the 6-digit code shown for Rush Studio.
                    </p>
                    {error && <div className="auth-error">{error}</div>}
                    <form className="auth-form" onSubmit={handleSubmit}>
                        <label className="field">
                            <span>Verification code</span>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="\d{6}"
                                className="input input-lg"
                                placeholder="123 456"
                                required
                                autoFocus
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            />
                        </label>
                        <button
                            className="btn btn-accent auth-submit"
                            type="submit"
                            disabled={submitting || code.length !== 6}
                        >
                            {submitting ? 'Verifying…' : 'Verify and continue'}
                        </button>
                    </form>
                    <p className="auth-sub" style={{ marginTop: 16, fontSize: 13 }}>
                        Lost your authenticator? Contact your workspace admin to reset it.
                    </p>
                </div>
            </section>
        </div>
    );
};

export default RushTwoFactorChallengePage;
